import posthog from 'posthog-js';
import { CURRENT_ENV, CURRENT_ENV_ROLE_TYPE, RELEASE_VERSION } from './EnvConfigUtils';

/**
 * Fundamental structured-logging layer for the web app.
 *
 * Every deliberate "something looks wrong / worth watching" signal from the
 * frontend flows through here into PostHog Logs (`posthog.captureLog`). One
 * canonical chokepoint with a consistent envelope means a single PostHog Logs
 * view/alert can watch the whole stream and route to Slack, and triage is done
 * by the stable `code` attribute rather than by parsing free-text.
 *
 * Design rules:
 *  - Add every new signal to `LOG_EVENTS` — never inline a raw code string at a
 *    call site. The registry keeps the funnel from fragmenting over typos and
 *    documents, in one place, every signal the FE can emit (mirrors WIZARD_NAMES).
 *  - `code` is the machine key alerts/dashboards group on; `domain` buckets codes
 *    by feature area; `message` is human-readable for the log explorer.
 *  - Telemetry must NEVER break a user flow — every emit is wrapped so a logging
 *    failure (or an SDK that predates `captureLog`) is swallowed silently.
 */

// Feature areas a log can belong to. Keep coarse — one bucket per surface.
export const LOG_DOMAINS = {
	WORK_ORDER_CREATION: 'work_order_creation',
} as const;

export type LogDomain = typeof LOG_DOMAINS[keyof typeof LOG_DOMAINS];

export interface LogEvent {
	code: string;
	domain: LogDomain;
}

// The registry of every structured log the FE can emit. `code` values are stable
// slugs — treat them as an API (PostHog views, alerts and Slack routing key off
// them), so rename with care.
export const LOG_EVENTS = {
	// Duplicate-check ran but the wizard had no target locationId, so the backend
	// falls back to a location-unscoped title search — the exact condition behind
	// the "duplicate from another club" report (OPE-5080).
	WO_DUPLICATE_CHECK_MISSING_LOCATION: {
		code: 'wo_duplicate_check_missing_location',
		domain: LOG_DOMAINS.WORK_ORDER_CREATION,
	},
	// Duplicate-check returned at least one candidate whose location differs from
	// the work order being created — a cross-club false positive that should never
	// be surfaced.
	WO_DUPLICATE_CROSS_LOCATION_CANDIDATE: {
		code: 'wo_duplicate_cross_location_candidate',
		domain: LOG_DOMAINS.WORK_ORDER_CREATION,
	},
} as const;

// PostHog Logs severities we use (subset of the SDK's trace|debug|info|warn|error|fatal).
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface StructuredLogInput {
	level: LogLevel;
	event: LogEvent;
	message: string;
	attributes?: Record<string, any>;
}

// Resource-level context stamped on every log so a raw log line is triageable on
// its own (which build/role/env produced it) without joining to a person. Service
// is just "web" (buyer/supplier share one app); the role is a separate `userType`
// attribute so it can be filtered independently.
const baseAttributes = () => ({
	service: 'web',
	userType: CURRENT_ENV_ROLE_TYPE,
	env: CURRENT_ENV,
	platform: 'web',
	appVersion: RELEASE_VERSION,
});

const emit = ({ level, event, message, attributes }: StructuredLogInput): void => {
	try {
		if (!posthog || typeof posthog.captureLog !== 'function') return;
		posthog.captureLog({
			body: message,
			level,
			attributes: {
				code: event.code,
				domain: event.domain,
				...baseAttributes(),
				...(attributes || {}),
			},
		});
	} catch (err) {
		// Telemetry must never affect the user flow — swallow deliberately.
	}
};

export const logInfo = (event: LogEvent, message: string, attributes?: Record<string, any>) =>
	emit({ level: 'info', event, message, attributes });

export const logWarn = (event: LogEvent, message: string, attributes?: Record<string, any>) =>
	emit({ level: 'warn', event, message, attributes });

export const logError = (event: LogEvent, message: string, attributes?: Record<string, any>) =>
	emit({ level: 'error', event, message, attributes });
